import { z } from 'zod';

/**
 * Блок 11 «Личный кабинет», экран 2.11.4 — контракт реферальной программы.
 * ADR-0094, PR `feature/block-11-referral` (фаза 11.D, фронт-ферст на MSW-моках).
 * **Последний экран Блока 11.** Любое изменение → новый ADR (P-011).
 *
 * Рефералка = ПДн-смежное (счётчик привязан к пользователю) → региональный
 * Postgres (P-007). Реального эндпоинта ещё нет → 11.D фронт на MSW-моке
 * `GET /api/referral`. Этот контракт задаёт форму ответа.
 *
 * ── `GET /api/referral` — приватный, БЕЗ тела/input (§1 ADR) ──────────────────
 * Пользователь берётся **из JWT** (P-006/P-007), не из тела — у эндпоинта нет
 * входного payload (только GET). Поэтому в файле НЕТ `*InputSchema` — только
 * `ReferralResponseSchema`.
 *
 * ── Минимизация ПДн (P-009) ──────────────────────────────────────────────────
 * Ответ несёт **только счётчики** — НЕ имена/телефоны/email друзей. Раскрытие
 * ПДн приглашённых не нужно для функции «покажи свой прогресс к скидке» и
 * запрещено минимизацией (152-ФЗ / Закон РК / Закон РУз). `inviteUrl` —
 * собственная ссылка пользователя (его реферальный код), не чужие данные.
 *
 * ── Инвариант `paidReferralsCount ≤ referralsCount` (гарантирует бэк) ─────────
 * Оплатившие — подмножество зарегистрировавшихся. Бонус `REFERRAL_INVITER`
 * (−50%, 1/мес) начисляется **только за ОПЛАТИВШИХ** друзей. Zod один это не
 * выразит (cross-field) — проверка на бэке; фронт перепроверяет тестом и при
 * показе берёт оба числа из одного ответа.
 *
 * ── `inviteUrl` строит бэк, `bonusAvailable` решает бэк ──────────────────────
 * Формат ссылки (реферальный код пользователя) — на бэке; фронт НЕ выдумывает,
 * только показывает/шарит (`z.string().url()` — единственная фронт-валидация).
 * `bonusAvailable` — был ли оплативший друг и не использован ли бонус в этом
 * месяце — целиком серверное правило; фронт показывает результат, сам не считает.
 *
 * References:
 *   - ADR-0094 §1; PRD `15-block-11-cabinet.md` §2.11.4; `01-foundation.md` §1.7
 *   - паттерн контракта/клиента: `contracts/profile.ts`, `contracts/billing.ts`
 *   - `REFERRAL_INVITER` (−50%) применяется на экране подписки 11.C (`billing.ts`)
 *   - policies: P-007, P-009, P-011, P-012, P-013
 */

// ── Ответ `GET /api/referral` — ТОЛЬКО счётчики (P-009), без ПДн друзей ────────
export const ReferralResponseSchema = z.object({
  // Персональная ссылка-приглашение (бэк строит с реферальным кодом пользователя).
  inviteUrl: z.string().url(),
  // Сколько друзей ЗАРЕГИСТРИРОВАЛИСЬ по ссылке (счётчик, не список — P-009).
  referralsCount: z.number().int().nonnegative(),
  // Из них СКОЛЬКО ОПЛАТИЛИ (бонус — только от оплативших). ≤ referralsCount (бэк).
  paidReferralsCount: z.number().int().nonnegative(),
  // Доступен ли REFERRAL_INVITER −50% в этом месяце (решает бэк, не фронт).
  bonusAvailable: z.boolean(),
});
export type ReferralResponse = z.infer<typeof ReferralResponseSchema>;
